import audioCache from '@/services/audio-cache';
import mobileAudio, { SoundCategory } from '@/services/mobile-audio';
import { defaultSounds } from '@/components/game/soundlist';
import { Capacitor } from '@capacitor/core';
import { audioStorageService } from '@/services/audio-storage-service';

// Check if we're running on a native mobile platform
const isNative = Capacitor.isNativePlatform();

/**
 * Plays a game sound by event key, checking for custom sounds first
 * and falling back to default sounds
 * 
 * @param soundKey The event key to trigger a sound for (e.g., 'gameplay.cardPlace')
 * @returns Promise that resolves when the sound starts playing
 */
export function playGameSound(soundKey: string): Promise<void> {
  try {
    console.log(`🔊 Attempting to play sound: ${soundKey}`);
    
    // Check if sound is muted in localStorage
    const soundMuted = localStorage.getItem('sound-effects-muted');
    if (soundMuted === 'true') {
      console.log(`🔊 Sound ${soundKey} not played because sounds are muted`);
      return Promise.resolve();
    }
    
    // Always force user interaction flag (helps with browser autoplay policies)
    document.documentElement.setAttribute('data-user-interacted', 'true');
    
    // Get the default sound URL
    const defaultUrl = defaultSounds[soundKey];
    if (!defaultUrl) {
      console.warn(`No default sound found for key: ${soundKey}`);
      return Promise.resolve();
    }
    
    // Ensure sound is loaded before attempting to play
    const ensureSoundLoaded = async (): Promise<void> => {
      // Check if sound is already cached
      if (!audioCache.isCached(soundKey)) {
        console.log(`🔊 Sound ${soundKey} not found in cache, preloading now...`);
        try {
          await audioCache.preloadAudio(soundKey, defaultUrl);
        } catch (err) {
          console.warn(`Failed to preload sound ${soundKey}`, err);
        }
      }
    };
    
    // Use our mobile-optimized audio service if on a native platform
    if (isNative) {
      return ensureSoundLoaded().then(() => {
        console.log(`🔊 Playing sound on mobile device: ${soundKey}`);
        return mobileAudio.playSound(soundKey);
      });
    }
    
    // On web, use the existing audio cache directly
    return ensureSoundLoaded().then(() => {
      console.log(`🔊 Playing sound on web: ${soundKey}`);
      return audioCache.play(soundKey);
    });
  } catch (error) {
    console.error(`Error playing game sound ${soundKey}:`, error);
    return Promise.resolve();
  }
}

/**
 * Plays background music (especially for gameplay)
 * 
 * @param key The background music key (e.g., 'gameplay1', 'menu1')
 * @param options Options for playback
 */
export function playBackgroundMusic(
  key: string, 
  options: { loop?: boolean; volume?: number } = {}
): Promise<void> {
  // Always force user interaction flag
  document.documentElement.setAttribute('data-user-interacted', 'true');
  
  if (isNative) {
    return mobileAudio.playBackgroundMusic(key, options);
  }
  
  // Fallback for web - use standard audio
  try {
    const audio = new Audio(`/sounds/background/${key}.mp3`);
    audio.loop = options.loop ?? false; // Default to false (no looping) unless explicitly set to true
    audio.volume = options.volume ?? 0.3;
    
    // Resume AudioContext if it's suspended
    const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    if (audioCtx.state === 'suspended') {
      audioCtx.resume();
    }
    
    return audio.play().catch(error => {
      console.warn(`Failed to play background music: ${key}`, error);
    });
  } catch (error) {
    console.error(`Error playing background music ${key}:`, error);
    return Promise.resolve();
  }
}

/**
 * Pauses currently playing background music
 */
export function pauseBackgroundMusic(): void {
  if (isNative) {
    mobileAudio.pauseBackgroundMusic();
  }
}

/**
 * Resumes paused background music
 */
export function resumeBackgroundMusic(): Promise<void> {
  if (isNative) {
    return mobileAudio.resumeBackgroundMusic();
  }
  return Promise.resolve();
}

/**
 * Stops background music completely
 */
export function stopBackgroundMusic(): void {
  if (isNative) {
    mobileAudio.stopBackgroundMusic();
  }
}

/**
 * Preloads game sounds to avoid first-play latency
 * 
 * @param categories Optional list of sound categories to preload. If not provided, preloads UI sounds.
 * @returns Promise that resolves when the preloading is complete
 */
export async function preloadGameSounds(categories?: SoundCategory[] | string[]): Promise<void> {
  console.log('🔊 Starting to preload game sounds...');
  
  // Always force user interaction flag (helps with browser autoplay policies)
  document.documentElement.setAttribute('data-user-interacted', 'true');
  
  // Default to preloading UI sounds if no categories specified
  const soundCategories = categories || [SoundCategory.UI];
  
  // Resume AudioContext if it's suspended
  try {
    const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    if (audioCtx && audioCtx.state === 'suspended') {
      console.log('🔊 Resuming suspended AudioContext...');
      await audioCtx.resume();
    }
  } catch (err) {
    console.warn('🔊 AudioContext not available or failed to resume:', err);
  }
  
  if (isNative) {
    try {
      // Use mobile audio service for more efficient loading on mobile
      console.log(`🔊 Preloading sounds for ${soundCategories.length} categories on mobile...`);
      const preloadPromises = soundCategories.map(category => 
        mobileAudio.preloadSoundsForCategory(category as SoundCategory)
          .catch(error => {
            console.warn(`🔊 Error preloading category ${category} on mobile:`, error);
          })
      );
      
      await Promise.all(preloadPromises);
      console.log('🔊 Mobile sound preloading complete');
      
      // Download sounds for offline use if on mobile
      if (Capacitor.isNativePlatform()) {
        try {
          console.log('🔊 Downloading sounds for offline use...');
          soundCategories.forEach(category => {
            // Download sounds for this category in the background
            audioStorageService.downloadCategory(category as string, (progress) => {
              if (progress === 100) {
                console.log(`🔊 Category ${category} downloaded for offline use`);
              }
            }).catch(error => {
              console.warn(`Error downloading sound category ${category}:`, error);
            });
          });
        } catch (error) {
          console.warn('Error downloading sounds for offline use:', error);
        }
      }
    } catch (err) {
      console.error('🔊 Error in mobile sound preloading:', err);
    }
  } else {
    try {
      // On web, preload individual sounds
      const soundKeys = Object.keys(defaultSounds).filter(key => {
        // Filter by categories if specified
        if (soundCategories.length > 0) {
          const category = key.split('.')[0];
          return soundCategories.some(c => c === category || c === key);
        }
        return true;
      });
      
      console.log(`🔊 Preloading ${soundKeys.length} sounds on web...`);
      
      // Create preload promises
      const preloadPromises = soundKeys.map(key => {
        const defaultUrl = defaultSounds[key];
        if (!defaultUrl) {
          console.log(`🔊 No URL for sound key: ${key}, skipping preload`);
          return Promise.resolve();
        }
        
        console.log(`🔊 Preloading sound: ${key} (${defaultUrl})`);
        
        // Create a fresh Audio element for each sound to test them
        const audio = new Audio(defaultUrl);
        audio.preload = 'auto';
        
        // Preload test with real Audio API
        const loadPromise = new Promise<void>((resolve) => {
          audio.addEventListener('canplaythrough', () => {
            console.log(`🔊 Successfully loaded ${key} with Audio element`);
            resolve();
          }, { once: true });
          
          audio.addEventListener('error', (err) => {
            console.warn(`🔊 Failed initial Audio preload for ${key}:`, err);
            // Fall back to audioCache method
            audioCache.preloadAudio(key, defaultUrl)
              .then(() => resolve())
              .catch(() => resolve()); // Always resolve to avoid blocking
          }, { once: true });
          
          // Set a timeout in case the audio takes too long to load
          setTimeout(() => {
            console.log(`🔊 Preload timeout for ${key}, continuing anyway`);
            resolve();
          }, 3000);
        });
        
        return loadPromise;
      });
      
      // Wait for all preloads to complete
      await Promise.all(preloadPromises);
      console.log('🔊 Web sound preloading complete');
    } catch (err) {
      console.error('🔊 Error in web sound preloading:', err);
    }
  }
  
  // Explicitly test a sound to verify sound system is working
  try {
    console.log('🔊 Testing sound system with ui.button...');
    //await playGameSound('ui.button');
    console.log('🔊 Sound test complete');
  } catch (err) {
    console.error('🔊 Sound test failed:', err);
  }
  
  console.log('🔊 Finished preloading game sounds');
}

/**
 * Mutes or unmutes all game sounds
 * 
 * @param muted Whether to mute (true) or unmute (false) sounds
 */
export function setGameSoundsMuted(muted: boolean): void {
  console.log(`🔊 Setting game sounds muted: ${muted}`);
  
  // Update localStorage first for persistence
  localStorage.setItem('sound-effects-muted', muted.toString());
  
  if (isNative) {
    mobileAudio.setSoundEffectsMuted(muted);
  } else {
    audioCache.setMuted(muted);
  }
}

/**
 * Mutes or unmutes background music
 * 
 * @param muted Whether to mute (true) or unmute (false) background music
 */
export function setBackgroundMusicMuted(muted: boolean): void {
  console.log(`🔊 Setting background music muted: ${muted}`);
  
  // Update localStorage first for persistence
  localStorage.setItem('background-music-muted', muted.toString());
  
  if (isNative) {
    mobileAudio.setBackgroundMusicMuted(muted);
  }
}

/**
 * Sets the volume for game sounds
 * 
 * @param volume Volume level between 0 and 1
 */
export function setGameSoundsVolume(volume: number): void {
  if (isNative) {
    mobileAudio.setSoundEffectsVolume(volume);
  } else {
    audioCache.setGlobalVolume(volume);
  }
}

/**
 * Sets the volume for background music
 * 
 * @param volume Volume level between 0 and 1
 */
export function setBackgroundMusicVolume(volume: number): void {
  console.log(`🔊 Setting background music volume: ${volume}`);
  
  // Update localStorage first for persistence
  localStorage.setItem('background-music-volume', volume.toString());
  
  if (isNative) {
    mobileAudio.setBackgroundMusicVolume(volume);
  }
}

/**
 * Gets the current mute state
 * 
 * @returns True if sounds are muted, false otherwise
 */
export function areGameSoundsMuted(): boolean {
  // Check localStorage first for the single source of truth
  const soundMuted = localStorage.getItem('sound-effects-muted');
  if (soundMuted !== null) {
    return soundMuted === 'true';
  }
  
  // Fall back to audioCache.isMuted if localStorage value is not set
  return audioCache.isMuted;
}

/**
 * Gets the current volume level
 * 
 * @returns Volume level between 0 and 1
 */
export function getGameSoundsVolume(): number {
  return audioCache.getGlobalVolume();
}

/**
 * Manually initializes the sound system.
 * Call this function from user interactions (like button clicks)
 * to ensure sounds can play in browsers with strict autoplay policies.
 */
export function initSoundSystem(): void {
  console.log('🔊 Initializing sound system...');
  
  // Set user interaction flag
  document.documentElement.setAttribute('data-user-interacted', 'true');
  audioCache.markUserInteraction();
  
  // Get mute settings from localStorage
  const soundMuted = localStorage.getItem('sound-effects-muted') === 'true';
  
  console.log(`🔊 Sound system initializing with mute state: ${soundMuted}`);
  
  // Apply settings to audio system
  if (isNative) {
    mobileAudio.setSoundEffectsMuted(soundMuted);
    mobileAudio.setBackgroundMusicMuted(soundMuted);
  } else {
    audioCache.setMuted(soundMuted);
  }
  
  // Create and resume audio context if available
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (AudioContextClass) {
      const context = new AudioContextClass();
      if (context.state === 'suspended') {
        context.resume().then(() => {
          console.log('🔊 AudioContext resumed successfully');
        }).catch(err => {
          console.warn('🔊 Failed to resume AudioContext:', err);
        });
      }
    }
  } catch (err) {
    console.warn('🔊 Error initializing AudioContext:', err);
  }
  
  // Only play test sound if not muted
  if (!soundMuted) {
    // Test a sound
    playGameSound('ui.button')
      .then(() => {
        console.log('🔊 Test sound played successfully');
      })
      .catch(err => {
        console.warn('🔊 Test sound failed:', err);
      });
  }
}